// SKILLSMITH-HUB in the in-memory API: the operator console's Agent health
// (`operator.agentHealth.get|topic|message|review|fix`), with the real routes' rules — platform
// super admins only (404 to anyone else; the twin's super admin is an agency `admin`), granted
// companies only, reports sorted severity (critical first) then company name, a fix applied or
// rejected only while proposed and reverted only once applied (409 otherwise). The demo companies
// have no system agent: every list starts empty. `fileReport` (the `__companyosFakeSkillsmith`
// hook) files a report as a company's Skillsmith would, creating that agent on first use.
import { MEMORY_QUALITY_THRESHOLDS } from "@companyos/schema/contracts";
import type { OperationInput, OperationOutput, RouteId } from "@companyos/sdk";
import { type Ctx, type Handler, json, notFound, problem, validation } from "./fake";
import { type FakeCompany, uid } from "./fake-data";

type Health = OperationOutput<"operator.agentHealth.get">;
type Topic = OperationOutput<"operator.agentHealth.topic">;
type Report = OperationOutput<"operator.agentHealth.review">;
type Message = OperationOutput<"operator.agentHealth.message">;
type Severity = Report["severity"];
type Hub = Health["companies"][number]["company"];

/** What a test files: the report's fields, as `operator.systemAgents.feedback.create` takes them. */
export type FakeSkillsmithReport = Omit<
  OperationInput<"operator.systemAgents.feedback.create">["body"],
  "company_id" | "issue_ids"
> & { created_at?: string };

const RANK: Record<Severity, number> = { critical: 0, high: 1, medium: 2, low: 3 };
const SEVERITIES = ["critical", "high", "medium", "low"] as const;

export const FAKE_SEVERITY_CRITERIA: Health["criteria"] = {
  critical: "Client-facing harm, a data or security risk, money, or work stopped.",
  high: "Repeated failures, or a KPI at risk.",
  medium: "A quality issue or a skill gap.",
  low: "Polish or an idea.",
};

type Agent = { membership_id: string; name: string; status: string };

export function createAgentHealthFake(deps: { now: () => Date; companies: () => FakeCompany[] }) {
  let seq = 0;
  const agents = new Map<string, Agent[]>();
  const topics = new Map<string, string>();
  const reports: Report[] = [];
  const messages: { company_id: string; membership_id: string; message: Message }[] = [];

  const hub = (c: FakeCompany): Hub => ({ id: c.id, slug: c.slug, name: c.name });
  const companyOf = (id: string) => deps.companies().find((c) => c.id === id) ?? null;

  /** The companies a super admin reads here (null: not a super admin, so 404). */
  function grantedOf(ctx: Ctx): Set<string> | null {
    const agency = ctx.me.agency;
    if (agency?.role !== "admin") return null;
    return new Set(agency.grants.map((g) => g.company_id));
  }

  function ensureAgent(companyId: string): Agent {
    const list = agents.get(companyId) ?? [];
    if (!list[0]) {
      list.push({ membership_id: uid(0xa9e0, ++seq), name: "Skillsmith", status: "active" });
      agents.set(companyId, list);
      topics.set(companyId, uid(0xa9e1, ++seq));
    }
    return list[0] as Agent;
  }

  function fileReport(companyId: string, r: FakeSkillsmithReport): Report {
    const agent = ensureAgent(companyId);
    const report: Report = {
      id: uid(0xa9e2, ++seq),
      company_id: companyId,
      membership_id: agent.membership_id,
      topic_id: topics.get(companyId) as string,
      kind: r.kind,
      severity: r.severity,
      title: r.title,
      body: r.body,
      evidence: r.evidence ?? [],
      affected_agents: r.affected_agents ?? [],
      suggested_fix: r.suggested_fix ?? null,
      proposal: r.proposal ?? null,
      fix_status: r.proposal ? "proposed" : "none",
      fix_decided_at: null,
      reviewed_at: null,
      created_at: r.created_at ?? deps.now().toISOString(),
    };
    reports.push(report);
    return report;
  }

  const counts = (rows: Report[]) =>
    Object.fromEntries(
      SEVERITIES.map((s) => [s, rows.filter((r) => r.severity === s).length]),
    ) as Health["totals"];

  /** The report a super admin of a granted company acts on (null: 404). */
  function reportFor(ctx: Ctx): Report | null {
    const granted = grantedOf(ctx);
    if (!granted?.has(ctx.params.company_id as string)) return null;
    return (
      reports.find(
        (r) => r.id === ctx.params.report_id && r.company_id === ctx.params.company_id,
      ) ?? null
    );
  }

  // MEMORY-QUALITY-WATCH: findings an operator reads and the thresholds the watch uses, per company
  type MqView = OperationOutput<"operator.memoryQuality.get">;
  const findings = new Map<string, MqView["findings"]>();
  const thresholds = new Map<string, Map<string, number>>();
  const mqView = (c: FakeCompany): MqView => {
    const mine = thresholds.get(c.id) ?? new Map<string, number>();
    return {
      company: hub(c),
      as_of: null,
      metrics: null,
      thresholds: MEMORY_QUALITY_THRESHOLDS.map((m) => ({
        metric: m.key,
        label: m.label,
        unit: m.unit,
        dir: m.dir,
        value: mine.get(m.key) ?? m.default,
        default: m.default,
        custom: mine.has(m.key) && mine.get(m.key) !== m.default,
      })),
      findings: findings.get(c.id) ?? [],
      facts: { stale_records: null, suggestions_open: 0, checked_at: null },
    };
  };
  /** What a test files: a finding the hourly watch raised for a company. */
  function fileMemoryFinding(
    companyId: string,
    f: Pick<MqView["findings"][number], "kind" | "severity" | "title"> & { suggestion?: string },
  ): MqView["findings"][number] {
    const row: MqView["findings"][number] = {
      id: uid(0xa9e5, ++seq),
      source: "watch",
      kind: f.kind,
      scope_key: "company",
      severity: f.severity,
      state: "open",
      title: f.title,
      suggestion: f.suggestion ?? null,
      value: null,
      threshold: null,
      detail: {},
      first_seen_at: deps.now().toISOString(),
      last_seen_at: deps.now().toISOString(),
    };
    findings.set(companyId, [row, ...(findings.get(companyId) ?? [])]);
    return row;
  }

  const handlers: Partial<Record<RouteId, Handler>> = {
    "operator.memoryQuality.get": (ctx) => {
      const granted = grantedOf(ctx);
      const co = companyOf(ctx.params.company_id ?? "");
      if (!granted || !co || !granted.has(co.id)) return notFound();
      return json(mqView(co));
    },
    "operator.memoryQuality.put": (ctx) => {
      const granted = grantedOf(ctx);
      const co = companyOf(ctx.params.company_id ?? "");
      if (!granted || !co || !granted.has(co.id)) return notFound();
      const b = (ctx.body ?? {}) as {
        thresholds?: Record<string, number | null>;
        dismiss?: string[];
      };
      const mine = thresholds.get(co.id) ?? new Map<string, number>();
      for (const [k, n] of Object.entries(b.thresholds ?? {})) {
        if (!MEMORY_QUALITY_THRESHOLDS.some((m) => m.key === k))
          return validation("an unknown threshold");
        if (n === null) mine.delete(k);
        else mine.set(k, n);
      }
      thresholds.set(co.id, mine);
      findings.set(
        co.id,
        (findings.get(co.id) ?? []).map((f) =>
          (b.dismiss ?? []).includes(f.id) && f.state === "open"
            ? { ...f, state: "dismissed" as const }
            : f,
        ),
      );
      return json(mqView(co));
    },
    "operator.agentHealth.get": (ctx) => {
      const granted = grantedOf(ctx);
      if (!granted) return notFound();
      const q = ctx.url.searchParams;
      const since = q.get("since") ?? new Date(deps.now().getTime() - 86_400_000).toISOString();
      const severities = q.get("severity")?.split(",") ?? null;
      const companyId = q.get("company_id");
      const status = q.get("status") ?? "all";
      const fresh = reports.filter((r) => granted.has(r.company_id) && r.created_at >= since);
      const name = (id: string) => companyOf(id)?.name ?? "";
      const rows = fresh
        .filter((r) => !severities || severities.includes(r.severity))
        .filter((r) => !companyId || r.company_id === companyId)
        .filter((r) =>
          status === "unreviewed"
            ? r.reviewed_at === null
            : status === "reviewed"
              ? r.reviewed_at !== null
              : true,
        )
        .sort(
          (a, b) =>
            RANK[a.severity] - RANK[b.severity] ||
            name(a.company_id).localeCompare(name(b.company_id)) ||
            b.created_at.localeCompare(a.created_at),
        );
      const companies = [...agents.keys()]
        .filter((id) => granted.has(id))
        .map((id) => companyOf(id))
        .filter((c): c is FakeCompany => c !== null)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((c) => {
          const mine = fresh.filter((r) => r.company_id === c.id);
          return {
            company: hub(c),
            topic_id: topics.get(c.id) ?? null,
            counts: counts(mine),
            total: mine.length,
            unreviewed: mine.filter((r) => r.reviewed_at === null).length,
            open_issues: 0,
            agents: agents.get(c.id) ?? [],
            budget: null,
          };
        });
      const inbox = reports
        .filter((r) => granted.has(r.company_id) && r.severity === "critical" && !r.reviewed_at)
        .sort((a, b) => b.created_at.localeCompare(a.created_at))
        .map((r) => {
          const c = companyOf(r.company_id) as FakeCompany;
          return {
            id: uid(0xa9e3, reports.indexOf(r) + 1),
            kind: "report" as const,
            company: hub(c),
            report_id: r.id,
            severity: r.severity,
            title: r.title,
            created_at: r.created_at,
          };
        });
      const body: Health = {
        since,
        criteria: FAKE_SEVERITY_CRITERIA,
        reports: rows.map((r) => ({ ...r, company: hub(companyOf(r.company_id) as FakeCompany) })),
        companies,
        totals: counts(fresh),
        inbox,
      };
      return json(body);
    },

    "operator.agentHealth.topic": (ctx) => {
      const granted = grantedOf(ctx);
      const id = ctx.params.company_id as string;
      const c = companyOf(id);
      if (!granted?.has(id) || !c) return notFound();
      const items: Topic["items"] = [
        ...messages
          .filter((m) => m.company_id === id)
          .map((m) => ({
            type: "message" as const,
            at: m.message.created_at,
            membership_id: m.membership_id,
            message: m.message,
          })),
        ...reports
          .filter((r) => r.company_id === id)
          .map((r) => ({ type: "report" as const, at: r.created_at, report: r })),
      ].sort((a, b) => a.at.localeCompare(b.at));
      const lastAgent = messages
        .filter((m) => m.company_id === id && m.message.author_kind === "system_agent")
        .at(-1)?.message.created_at;
      const body: Topic = {
        company: hub(c),
        topic_id: topics.get(id) ?? null,
        agents: agents.get(id) ?? [],
        items,
        pending: messages.filter(
          (m) =>
            m.company_id === id &&
            m.message.author_kind === "super_admin" &&
            (!lastAgent || m.message.created_at > lastAgent),
        ).length,
        open_issues: 0,
        budget: null,
      };
      return json(body);
    },

    "operator.agentHealth.message": (ctx) => {
      const granted = grantedOf(ctx);
      const id = ctx.params.company_id as string;
      if (!granted?.has(id) || !companyOf(id)) return notFound();
      const b = (ctx.body ?? {}) as { body?: string; membership_id?: string };
      if (!b.body?.trim()) return validation("body is required");
      const list = agents.get(id) ?? [];
      const agent = b.membership_id
        ? list.find((a) => a.membership_id === b.membership_id)
        : (list.find((a) => a.status === "active") ?? list[0]);
      if (!agent) return notFound();
      const message: Message = {
        id: uid(0xa9e4, ++seq),
        author_kind: "super_admin",
        author_user_id: ctx.me.user.id,
        kind: "message",
        investigation_id: null,
        body: b.body,
        created_at: deps.now().toISOString(),
      };
      messages.push({ company_id: id, membership_id: agent.membership_id, message });
      return json(message, 201);
    },

    "operator.agentHealth.review": (ctx) => {
      const r = reportFor(ctx);
      if (!r) return notFound();
      const b = (ctx.body ?? {}) as { reviewed?: unknown };
      if (typeof b.reviewed !== "boolean") return validation("reviewed must be a boolean");
      r.reviewed_at = b.reviewed ? deps.now().toISOString() : null;
      return json(r);
    },

    "operator.agentHealth.fix": (ctx) => {
      const r = reportFor(ctx);
      if (!r) return notFound();
      const action = (ctx.body as { action?: string } | null)?.action;
      if (!action || !["apply", "reject", "revert"].includes(action))
        return validation("action must be apply, reject or revert");
      const next =
        action === "apply" && r.fix_status === "proposed"
          ? "applied"
          : action === "reject" && r.fix_status === "proposed"
            ? "rejected"
            : action === "revert" && r.fix_status === "applied"
              ? "reverted"
              : null;
      if (!next) return problem(409, "conflict", "Conflict", `the fix is ${r.fix_status}`);
      r.fix_status = next;
      r.fix_decided_at = deps.now().toISOString();
      return json(r);
    },
  };

  return { handlers, fileReport, fileMemoryFinding };
}
